import { useEffect, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SpacesSlider() {
  const [spaces, setSpaces] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef(null);
  const { openModal } = useOutletContext();

  useEffect(() => {
    const fetchSpaces = async () => {
      try {
        const response = await axios.get(`${API}/spaces`);
        // Show all active spaces
        const activeSpaces = response.data.filter(s => s.is_active !== false);
        setSpaces(activeSpaces);
      } catch (error) {
        console.error('Error fetching spaces:', error);
      }
    };
    fetchSpaces();
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleEnquire = (space) => {
    openModal({
      form_type: 'space_enquiry',
      space_name: space.name,
      workspace_type: space.type,
    });
  };

  return (
    <section className="section-padding bg-[#E12429]" id="our-spaces" data-testid="spaces-slider-section">
      <div className="container-wide mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Our Spaces</h2>
          <p className="text-white/90 text-base max-w-2xl mx-auto">
            Choose from a variety of premium workspaces designed for every business need.
          </p>
        </div>

        <div className="relative">
          {/* Navigation Buttons */}
          {spaces.length > 2 && (
            <>
              <button
                onClick={() => scroll('left')}
                data-testid="spaces-prev-button"
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-all"
              >
                <ChevronLeft className="text-[#E12429]" size={24} />
              </button>
              <button
                onClick={() => scroll('right')}
                data-testid="spaces-next-button"
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-all"
              >
                <ChevronRight className="text-[#E12429]" size={24} />
              </button>
            </>
          )}

          {/* Slider */}
          <div
            ref={scrollRef}
            className="flex overflow-x-auto space-x-6 pb-4 hide-scrollbar scroll-smooth"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {spaces.map((space, index) => (
              <div
                key={space.id}
                className="flex-shrink-0 w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] bg-white rounded-lg overflow-hidden shadow-lg"
                style={{ scrollSnapAlign: 'start' }}
                data-testid={`space-card-${index}`}
              >
                <div className="relative h-56">
                  <img
                    src={space.images[0]}
                    alt={space.name}
                    className="w-full h-full object-cover"
                  />
                  {space.badges.length > 0 && (
                    <div className="absolute top-4 right-4">
                      {space.badges.map((badge, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-[#E12429] text-white text-xs font-semibold rounded-full"
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-[#222] mb-2">{space.name}</h3>
                  <p className="text-sm text-[#E12429] font-semibold mb-3">{space.type}</p>
                  <p className="text-[#666] text-sm mb-4 line-clamp-2">{space.description}</p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[#222] font-semibold">Capacity: {space.capacity}</span>
                    {space.price && (
                      <span className="text-[#E12429] font-bold">{space.price}</span>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => window.location.href = `/space/${space.id}`}
                      className="flex-1 btn-secondary text-sm py-2"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => handleEnquire(space)}
                      data-testid={`enquire-button-${index}`}
                      className="flex-1 btn-primary text-sm py-2"
                    >
                      Enquire Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}